import { removeHtmlTags } from '@lib/remove-html-tags'
import clsx from 'clsx'
import React, {
  CSSProperties,
  PropsWithChildren,
  Ref,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import s from './LineClampV2.module.scss'

interface Props {
  text: string
  lines: number
  fontSize: number
  mobileOnly?: boolean
}

type NativeAttrs = Omit<React.HTMLAttributes<any>, keyof Props>

export type LineClampProps = Props & NativeAttrs

const LineClampComponent = forwardRef<
  HTMLSpanElement,
  PropsWithChildren<LineClampProps>
>(
  (
    { text, fontSize, lines, mobileOnly, children, ...props },
    ref: Ref<HTMLSpanElement | null>,
  ) => {
    const [expanded, setExpanded] = useState(false)
    const spanRef = useRef<HTMLSpanElement>(null)
    const textRef = useRef<HTMLSpanElement>(null)
    useImperativeHandle(ref, () => spanRef.current)

    useEffect(() => {
      if (!spanRef.current) return
      const height = getComputedStyle(textRef.current).height
      spanRef.current.style.setProperty('--expanded-height', height)
    }, [spanRef.current])

    const toggle = () => {
      setExpanded(!expanded)
      spanRef.current.classList.toggle(s['expanded'])
    }

    return (
      <>
        <span
          className={clsx(s.root, props.className, {
            [s.mobileOnly]: mobileOnly,
          })}
          style={
            { '--intial-height': `${fontSize * lines}px` } as CSSProperties
          }
          ref={spanRef}
          {...props}
        >
          <span
            style={{ display: 'block' }}
            dangerouslySetInnerHTML={{ __html: removeHtmlTags(text) }}
            ref={textRef}
          />
        </span>
        <span className={clsx(s.button, { [s.mobileOnly]: mobileOnly })}>
          <button type="button" onClick={toggle}>
            {expanded ? 'Read less' : '... Read more'}
          </button>
        </span>
      </>
    )
  },
)

LineClampComponent.displayName = 'LineClamp'
const LineClamp = LineClampComponent
export default LineClamp
